import { TableCell } from '@mui/material';
import { memo } from 'react';
import { getColumnOptions } from '@utils/tan-table';
import { flexRender } from '@tanstack/react-table';
import { PiCaretDownFill } from 'react-icons/pi';
import type { CustomHeaderCellProps } from '@app/types/main-table';

import styles from './index.module.scss';

function MainTableHeadBasicComponent<TData extends Record<string, any>>({
  headerGroup,
  isLeftScrollable,
  isRightScrollable,
}: CustomHeaderCellProps<TData>) {
  if (!headerGroup) return null;

  return (
    <>
      {headerGroup.headers.map((header) => {
        const { canResize, canPin, isPinned, isLeftPinned, isRightPinned } =
          getColumnOptions(header.column);

        const headerCellClasses = [
          styles.headerCell,
          isPinned && canPin && styles.pinned,
          !isPinned && canResize && styles.canResize,
          isPinned && isLeftScrollable && styles.pinnedLeft,
          isPinned && isRightScrollable && styles.pinnedRight,
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <TableCell
            key={header.id}
            variant="head"
            className={headerCellClasses}
            sx={{
              left: isLeftPinned ? header.column.getStart('left') : undefined,
              right: isRightPinned
                ? header.column.getAfter('right')
                : undefined,
              width: header.getSize() ?? 'auto',
              textAlign: 'center',
            }}
          >
            {header.isPlaceholder
              ? null
              : flexRender(header.column.columnDef.header, header.getContext())}

            {header.column.getCanSort() &&
              (() => {
                const sortState = header.column.getIsSorted();
                const sortIconClasses = [
                  styles.sortIcon,
                  sortState === 'asc' && styles.sortIconAsc,
                  sortState === 'desc' && styles.sortIconDesc,
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <button
                    type="button"
                    onClick={header.column.getToggleSortingHandler()}
                    className={styles.sortButton}
                    aria-label="Sort by column"
                  >
                    <PiCaretDownFill size={14} className={sortIconClasses} />
                  </button>
                );
              })()}

            {header.column.getCanResize() && (
              <button
                type="button"
                className={styles.resizeHandle}
                onDoubleClick={() => header.column.resetSize()}
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    header.column.resetSize();
                  }
                }}
                aria-label="Resize column"
              />
            )}
          </TableCell>
        );
      })}
    </>
  );
}

const MainTableHeadBasic = memo(
  MainTableHeadBasicComponent
) as typeof MainTableHeadBasicComponent;

export default MainTableHeadBasic;
